import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import PC from './layout/pc';
import H5 from './layout/h5';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import type { BasePageState } from '../../types/base';
import { getCookie } from '@utils/cookie';
import Toast from '@components/Toast';
interface RouteParams {
    code: string;
}
interface UserInfo {
    code: string;
    nickname?: string;
    avatar?: string;
    follow?: number;
    isFollowed?: boolean;
    [key: string]: any;  // 其他可能的属性
}

interface MessageProps extends BasePageProps, RouteComponentProps<RouteParams> {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
}
interface MessageState extends BasePageState {
    myValue: string;
    userInfo: UserInfo
    navList: any
    navCurrentIndex: any
    navCurrentType: number
    filterValue: any    // 筛选消息的类型
    unreadNotificationsData: any
    chatList: any    // 私信列表
    // friendList: any // 关注/粉丝列表
    // friendType: any   // 类型（1是关注，为2是粉丝）
    // friendPage: any
}
@inject('app', 'site', 'user', 'thread')
@observer
class Message extends BasePage<MessageProps, MessageState> {
    namespace = 'userNotificationsList';    // 列表命名空间
    currentLoadingCode: string = '';        // 用于记录当前正在加载的用户 code，防止竞态条件
    isLoadingMore: boolean = false;         // 防止重复加载更多
    state = {
        loading: false,
        error: null,
        pageLoaded: false,
        myValue: '',
        userInfo: { code: '' },  // 提供默认值
        navList: [
            {name: '评论', id: 0, type: 0, filterValue: 'replied', num: 0 },
            {name: '@我', id: 1, type: 1, filterValue: 'related', num: 0},
            {name: '系统通知', id: 2, type: 2, filterValue: 'system,thread,post,trial_product,auction', num: 0},
            {name: '点赞和收藏', id: 3, type: 3, filterValue: 'liked,favorited', num: 0},
        ],  // tab切换列表
        filterValue: '',    // 筛选消息的类型
        navCurrentIndex: 0, // 导航当前选中索引
        navCurrentType: 0,  // 导航当前选中类型
        unreadNotificationsData: {},    // 未读消息数据
        chatList: [],    // 私信列表
    };
    componentDidMount() {
        const { user, match } = this.props;
        // 获取路由参数 code
        const code = match.params.code;

        // 从路由读取 tab 参数，匹配对应 tab 索引
        const routerParams = new URLSearchParams(this.props.location.search);
        const tabParam = Number(routerParams?.get('tab')) || 0;
        const targetIndex = this.state.navList.findIndex((item: any) => item.type === tabParam);
        const initialIndex = targetIndex !== -1 ? targetIndex : 0;
        const initialTab = this.state.navList[initialIndex];

        // 记录当前加载的用户 code，用于防止请求串联
        this.currentLoadingCode = code;

        // 根据路由参数设置初始 tab 状态
        if (initialIndex !== this.state.navCurrentIndex) {
            this.state.navCurrentIndex = initialIndex;
            this.state.navCurrentType = initialTab.type;
            this.state.filterValue = initialTab.filterValue;
        }

        // 先重置列表状态
        this.resetList();

        if (!user || !code) {
            super.componentDidMount();
            return;
        }

        // 获取用户信息
        user.getUserInfo({ code: code }).then(res => {
            // 竞态保护：如果用户已切换，忽略旧数据
            if (this.currentLoadingCode !== code) return;

            if (res.code === 0) {
                this.setState({
                    userInfo: res.data
                });
                // 按平台加载对应数据
                const { app } = this.props;
                if (app && app.platform === 'pc') {
                    this.loadTabDataByNamespace(code);
                } else if (app) {
                    // H5端：加载私信列表
                    this.loadTabDataByNamespace(code);
                    this.getUnreadNotifications();
                }
            }
        });
        super.componentDidMount();
    }

    componentDidUpdate(prevProps: MessageProps, prevState: MessageState) {
        // 路由参数变化（用户 code 切换）
        if (prevProps.match.params.code !== this.props.match.params.code) {
            const { user, match } = this.props;
            const code = match.params.code;
            
            // 更新当前加载的用户 code
            this.currentLoadingCode = code;

            // 重置列表状态
            this.resetList();

            if (!user || !code) return;

            // 重新获取新用户的信息
            user.getUserInfo({ code: code }).then(res => {
                if (this.currentLoadingCode !== code) return;

                if (res.code === 0) {
                    this.setState({
                        userInfo: res.data
                    });
                    // 按平台加载对应数据
                    const { app } = this.props;
                    if (app && app.platform === 'pc') {
                        this.loadTabDataByNamespace(code);
                    } else if (app) {
                        // H5端：加载私信列表
                        this.loadTabDataByNamespace(code);
                        this.getUnreadNotifications();
                    }
                }
            });
        }

        // tab 切换时重置加载状态
        if (prevState?.navCurrentIndex !== this.state.navCurrentIndex) {
            this.isLoadingMore = false;
        } 
    }
    componentWillUnmount(): void {
        const { app, user } = this.props;
        // 使仍在途中的未读请求失效，避免离开页面后继续更新状态
        this.currentLoadingCode = '';
        if (!user?.listStore?.clearList) return;
        // H5端清理 chatList，PC端清理 userNotificationsList
        const namespace = (app && app.platform !== 'pc') ? 'chatList' : 'userNotificationsList';
        user.listStore.clearList({ namespace });
    }

    // 获取未读消息数
    getUnreadNotifications = async () => {
        const { user } = this.props;
        if (!user) return;

        const requestCode = this.currentLoadingCode;
        const res = await user.getUnreadNotifications();
        if (res.code !== 0 || !res.data || requestCode !== this.currentLoadingCode) return;

        const typeUnreadNotifications = res.data.typeUnreadNotifications || {};
        // 将各通知类型汇总到对应 Tab，保证当前已读状态和其余未读角标同步更新
        const tabUnreadCount = {
            0: Number(typeUnreadNotifications.replied) || 0,
            1: Number(typeUnreadNotifications.related) || 0,
            2: ['system', 'thread', 'post', 'trial_product', 'auction'].reduce(
                (total, type) => total + (Number(typeUnreadNotifications[type]) || 0),
                0
            ),
            3: ['liked', 'favorited'].reduce(
                (total, type) => total + (Number(typeUnreadNotifications[type]) || 0),
                0
            ),
        };

        this.setState((prevState) => ({
            unreadNotificationsData: res.data,
            navList: prevState.navList.map((item: any) => ({
                ...item,
                num: tabUnreadCount[item.type] || 0,
            })),
        }));
    }

    // PC端列表读取成功后，同步顶部总角标和各分类未读角标
    refreshPCUnreadAfterListRead = async (code: string) => {
        const { app, user } = this.props;
        if (!app || app.platform !== 'pc' || !user) return;

        const currentUserCode = user.userInfo?.code || getCookie('user_code');
        if (currentUserCode) {
            // 重新获取当前登录用户 meta，使 Header 中的总未读数立即刷新
            await user.fetchUserInfo({ code: currentUserCode });
        }

        if (this.currentLoadingCode !== code) return;
        await this.getUnreadNotifications();
    }

    // 一键已读：清空所有未读消息
    submitNotificationsReadData = async () => {
        const { user } = this.props;
        if (!user) return;

        const res = await user.markAllNotificationsAsRead();
        if (res.code === 0) {
            this.getUnreadNotifications();
            Toast.info('清除消息成功');
        }
    }

    // 按当前 tab 的 filterValue 加载消息通知数据
    // filter[type]：system系统通知, related艾特@我的, replied回复我的, liked,favorited 点赞和收藏
    loadTabDataByNamespace = async (code: string) => {
        const { app, user } = this.props;
        const { navList, navCurrentIndex, filterValue } = this.state;
        console.log(app.platform, '平台平台')
        if (!user || !code) return;

        const currentTab = navList[navCurrentIndex];
        if (!currentTab) return;

        // H5端：调用 fetchMessageList 获取私信列表
        if (app && app.platform !== 'pc') {
            console.log('获取私信列表');
            return user.fetchChatList({
                page: 1,
                perPage: 20,
            });
        }

        // PC端：按 filterValue 加载消息通知列表
        if (!app || app.platform !== 'pc') return;

        // 先清空消息通知列表，避免残留旧数据
        user.listStore.clearList({ namespace: 'userNotificationsList' });

        const params = {
            'page[number]': 1,
            'page[size]': 20,
            'filter[type]': filterValue || currentTab.filterValue
        };

        const requestTabIndex = navCurrentIndex;
        const res = await user.fetchNotificationsList(params);
        if (res.code === 0) {
            // 服务端在读取当前分类列表后将其标记为已读，先即时移除该 Tab 角标
            this.setState((prevState) => ({
                navList: prevState.navList.map((item: any, index: number) => (
                    index === requestTabIndex ? { ...item, num: 0 } : item
                )),
            }));
            // 列表读取完成后再查询未读，避免并发请求把旧角标重新写回页面
            await this.refreshPCUnreadAfterListRead(code);
        }

        return res;
    }

    // 清空消息列表数据，避免复用上一次列表数据
    resetList = () => {
        const { app, user } = this.props;
        const store = user?.listStore;
        if (!store?.clearList) return;

        // H5端：使用 chatList 命名空间
        if (app && app.platform !== 'pc') {
            store.clearList({ namespace: 'chatList' });
            store.setAttribute?.({
                namespace: 'chatList',
                key: 'requestError',
                value: { isError: false, errorText: '' }
            });
            return;
        }

        // PC端：清空消息通知列表及错误状态
        if (!app || app.platform !== 'pc') return;
        store.clearList({ namespace: 'userNotificationsList' });
        store.setAttribute?.({
            namespace: 'userNotificationsList',
            key: 'requestError',
            value: { isError: false, errorText: '' }
        });
    }

    // 分页 - 加载更多消息通知 / 私信列表
    handleLoadMore = () => {
        const { app, user, match } = this.props;
        const { userInfo, navList, navCurrentIndex, filterValue } = this.state;

        if (!user) return;

        // H5端：加载更多私信列表
        if (app && app.platform !== 'pc') {
            const namespace = 'chatList';
            const list = user.listStore.lists[namespace];
            if (!list || !list.hasMore) return;
            if (this.isLoadingMore) return;

            const currentPage = list.attribs?.currentPage || list.attribs?.page || 1;
            const perPage = list.attribs?.pageSize || 20;
            const nextPage = currentPage + 1;

            this.isLoadingMore = true;

            user.fetchChatList({ page: nextPage, perPage })
                .finally(() => {
                    this.isLoadingMore = false;
                });
            return;
        }

        // PC端
        if (!app || app.platform !== 'pc') return;

        const code = match.params.code || userInfo.code;

        if (!code) return;

        const currentTab = navList[navCurrentIndex];
        if (!currentTab) return;

        const namespace = 'userNotificationsList';
        const list = user.listStore.lists[namespace];
        if (!list) return;

        // 判断是否还有更多数据
        if (!list.hasMore) return;

        // 防止重复加载
        if (this.isLoadingMore) return;

        // 获取分页信息
        const currentPage = list.attribs?.currentPage || list.attribs?.page || 1;
        const pageSize = list.attribs?.pageSize || 20;
        const nextPage = currentPage + 1;

        // 保存当前的 code 和 tab 索引，用于回调时验证
        const requestCode = code;
        const requestTabIndex = navCurrentIndex;

        this.isLoadingMore = true;

        const params: any = {
            'page[number]': nextPage,
            'page[size]': pageSize,
            'filter[type]': filterValue || currentTab.filterValue
        };

        user.fetchNotificationsList(params)
            .then((res: any) => {
                // 检查用户或tab是否已经切换
                const currentCode = this.props.match.params.code || this.state.userInfo.code;
                if (requestCode !== currentCode || requestTabIndex !== this.state.navCurrentIndex) {
                    return;
                }

                if (res.code === 0) {
                    // 数据已由 fetchNotificationsList 写入 listStore，无需额外 setState
                }
            })
            .catch((error: any) => {
                console.error('加载更多消息通知失败', error);
            })
            .finally(() => {
                this.isLoadingMore = false;
            });
    };

    // 导航点击，类型切换
    navClick = (item: any, index: any) => {
        const { app, match, history, location } = this.props;
        const code = match.params.code;

        // 再次点击当前 Tab 时也重新读取，确保新到消息同步变为已读
        if (this.state.navCurrentIndex === index) {
            if (app && app.platform === 'pc' && code) {
                this.loadTabDataByNamespace(code);
            }
            return;
        }

        // 重置加载状态
        this.isLoadingMore = false;

        // PC端同步路由上的 tab 参数
        if (app && app.platform === 'pc') {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set('tab', String(item.type));
            history.replace({ pathname: `/message/${code}`, search: searchParams.toString() });
        }

        // 更新 state 并加载对应 tab 数据
        this.setState({
            navCurrentIndex: index,
            navCurrentType: item.type,
            filterValue: item.filterValue,
        }, () => {
            const { app, user } = this.props;
            if (app && app.platform === 'pc' && user && code) {
                this.loadTabDataByNamespace(code);
            }
        });
    }

    // 加载tab数据（供外部调用）
    loadTabData = () => {
        const { app, user, match } = this.props;
        const { userInfo } = this.state;
        if (!app || app.platform !== 'pc') return;
        const code = match.params.code || userInfo.code;
        if (user && code) {
            this.loadTabDataByNamespace(code);
        }
    }

    render() {
        const { app } = this.props;
        let { userInfo, navList, navCurrentIndex, navCurrentType, unreadNotificationsData } = this.state;
        const platform = app?.platform || 'h5';
        // 如果平台信息还未初始化，显示加载状态
        if (!app?.platform) {
            return (
                <div className={styles.loading} style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}>
                    <div>加载中...</div>
                </div>
            );
        } else {
            return (<div className={platform === 'pc' ? styles.pcWrap : styles.h5Wrap}>
                {platform === 'pc' ? (<PC
                    userInfo={userInfo}
                    handleLoadMore={this.handleLoadMore}
                    navList={navList}
                    navCurrentIndex={navCurrentIndex}
                    navCurrentType={navCurrentType}
                    unreadNotificationsData={unreadNotificationsData}
                    navClick={(item, index) => this.navClick(item, index)}
                ></PC>) : (
                    <H5
                        userInfo={userInfo}
                        handleLoadMore={this.handleLoadMore}
                        unreadNotificationsData={unreadNotificationsData}
                        submitNotificationsReadData={this.submitNotificationsReadData}
                    ></H5>
                )}
            </div>)
        }

    }
}

export default withRouter(Message);
